<template>
  <div id="app">
    <DynamicModal />
    <LayoutBroker :name="layout">
      <router-view />
    </LayoutBroker>

    <!-- Persistent helper stays on '/' and '/login' until user navigates away or dismisses -->
    <div v-if="showHelper" class="login-helper">
      <p class="helper-text">
        If you’re unable to log in, copy the link below and paste it into Google Chrome:
      </p>

      <div class="copy-row">
        <input
          ref="copyInput"
          class="copy-input"
          :value="loginUrl"
          readonly
          @focus="$event.target.select()"
          @click="$event.target.select()"
        />
        <button class="copy-btn" @click="copy" :disabled="copied">
          <span v-if="!copied">Copy</span>
          <span v-else>Copied ✓</span>
        </button>
        <button class="dismiss-btn" @click="dismiss">Dismiss</button>
      </div>

      <small class="hint">Tip: tap the box to select, then press Copy.</small>
      <span class="sr-only" aria-live="polite">{{ copied ? 'Link copied' : '' }}</span>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import DynamicModal from '@/components/utilities/modal/DynamicModal.vue'
import LayoutBroker from '@/layouts/broker'

const route = useRoute()
const router = useRouter()

// Keep layout logic
const layout = computed(() => route.meta?.layout || 'no-nav')

// Absolute login URL (same host), redirecting to home (or use route.fullPath if you prefer)
const loginUrl = computed(() => {
  const href = router.resolve({ name: 'Login', query: { redirect: '/' } }).href
  return new URL(href, window.location.origin).toString()
})

// Visibility state persisted per-tab
const STORAGE_KEY = 'chromeLoginHelper:dismissed'
const dismissed = ref(sessionStorage.getItem(STORAGE_KEY) === '1')

// Consider helper-visible routes: root and /login only
const isRoot = computed(() => route.path === '/' && !Object.keys(route.query).length && !route.hash)
const isLogin = computed(() => route.name === 'Login' || route.path === '/login')

// Show when not dismissed AND we are on root or login
const showHelper = computed(() => !dismissed.value && (isRoot.value || isLogin.value))

// If user navigates away from root/login, hide permanently for this session
watch(() => route.fullPath, () => {
  if (!isRoot.value && !isLogin.value) {
    dismissed.value = true
    sessionStorage.setItem(STORAGE_KEY, '1')
  }
})

function dismiss() {
  dismissed.value = true
  sessionStorage.setItem(STORAGE_KEY, '1')
}

const copied = ref(false)
const copyInput = ref(null)
async function copy() {
  try {
    const text = loginUrl.value
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
    } else {
      const el = copyInput.value
      el?.focus(); el?.select()
      document.execCommand('copy')
    }
    copied.value = true
    setTimeout(() => (copied.value = false), 1500)
  } catch {
    copyInput.value?.focus()
    copyInput.value?.select()
    alert('Copy failed. Please select the text and copy it manually.')
  }
}
</script>

<style scoped>
.login-helper { margin: 1rem; padding: 1rem; border: 1px solid #e5e7eb; border-radius: 8px; background: #fafafa; }
.helper-text { margin: 0 0 .5rem; }
.copy-row { display: flex; gap: .5rem; align-items: stretch; flex-wrap: wrap; }
.copy-input {
  flex: 1; min-width: 260px; padding: .55rem .75rem; border: 1px solid #d1d5db; border-radius: 6px; background: #fff;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: .95rem;
}
.copy-input:focus { outline: 2px solid #93c5fd; outline-offset: 2px; }
.copy-btn { padding: 0 .9rem; border: 1px solid #2563eb; background: #2563eb; color: #fff; border-radius: 6px; font-weight: 600; cursor: pointer; }
.dismiss-btn { padding: 0 .9rem; border: 1px solid #6b7280; background: #fff; color: #374151; border-radius: 6px; font-weight: 600; cursor: pointer; }
.copy-btn:disabled { opacity: .7; cursor: default; }
.hint { display: block; margin-top: .35rem; color: #6b7280; }
.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0; }
</style>
